import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  CircularProgress, 
  Alert, 
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { DataGrid, GridColDef, GridSortModel, GridFilterModel } from '@mui/x-data-grid';
import { 
  Search as SearchIcon, 
  Clear as ClearIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ToggleOn, 
  ToggleOff, 
  Add as AddIcon 
} from '@mui/icons-material';
import { 
  getPromotions, 
  getPromotionById,
  updatePromotionStatus, 
  Promotion, 
  updatePromotion,
  deletePromotion
} from '../services/promotionService';
import AddPromotionDialog from '../components/promotions/AddPromotionDialog';
import EditPromotionDialog from '../components/promotions/EditPromotionDialog';

interface PromotionRow extends Promotion {
  // Add any additional fields needed for the row
}

export const PromotionsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<PromotionRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<PromotionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState<Promotion | null>(null);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'start_date', sort: 'desc' },
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  
  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const data = await getPromotions();
      setRows(data);
      setFilteredRows(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setError('Error al cargar las promociones. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        await fetchPromotions();
      } catch (err) {
        setError('Error al cargar las promociones. Por favor, intente nuevamente.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadPromotions();
  }, []);

  // Filter rows based on search text
  useEffect(() => {
    if (searchText === '') {
      setFilteredRows(rows);
    } else {
      const filtered = rows.filter(row => 
        Object.values(row).some(
          value => 
            value && 
            value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setFilteredRows(filtered);
    }
  }, [searchText, rows]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = (created: boolean = false) => {
    setAddDialogOpen(false);
    if (created) {
      fetchPromotions();
    }
  };

  const handleEditPromotion = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (promotion: Promotion) => {
    setPromotionToDelete(promotion);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!promotionToDelete) return;
    
    try {
      const result = await deletePromotion(promotionToDelete.id);
      if (result.success) {
        enqueueSnackbar('Promoción eliminada correctamente', { variant: 'success' });
        await fetchPromotions();
      } else {
        enqueueSnackbar(result.message || 'Error al eliminar la promoción', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error deleting promotion:', error);
      enqueueSnackbar('Error al eliminar la promoción', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setPromotionToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPromotionToDelete(null);
  };

  const handleSavePromotion = async (updatedPromotion: Partial<Promotion>, imageFile?: File) => {
    if (!selectedPromotion) return;
    
    try {
      setEditDialogOpen(false);
      setLoading(true);
      
      await updatePromotion(selectedPromotion.id, updatedPromotion, imageFile);
      await fetchPromotions();
      
      enqueueSnackbar('Promoción actualizada exitosamente', { variant: 'success' });
    } catch (err) {
      console.error('Error updating promotion:', err);
      enqueueSnackbar('Error al actualizar la promoción. Por favor, intente nuevamente.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    // Assuming dateString is in DD/MM/YYYY format
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Data URL for a transparent 1x1 pixel image
  const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  const columns: GridColDef<PromotionRow>[] = [
    { 
      field: 'id', 
      headerName: 'ID',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'img', 
      headerName: 'Imagen', 
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          component="img"
          src={params.value || transparentPixel}
          alt="Promoción"
          sx={{
            width: 50,
            height: 50,
            objectFit: 'cover',
            borderRadius: 1,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#666',
            textAlign: 'center',
            padding: '4px',
            boxSizing: 'border-box',
            '&::after': {
              content: '"Sin imagen"',
              display: !params.value ? 'block' : 'none',
            }
          }}
          onError={(e: any) => {
            e.target.onerror = null;
            e.target.src = transparentPixel;
            e.target.style.backgroundColor = '#f5f5f5';
            e.target.style.display = 'flex';
            e.target.style.alignItems = 'center';
            e.target.style.justifyContent = 'center';
            e.target.style.fontSize = '10px';
            e.target.style.color = '#666';
            e.target.style.textAlign = 'center';
            e.target.style.padding = '4px';
            e.target.style.boxSizing = 'border-box';
            e.target.setAttribute('data-error', 'true');
          }}
        />
      ),
    },
    { 
      field: 'title', 
      headerName: 'Título', 
      flex: 1,
      minWidth: 200,
    },
    { 
      field: 'channel', 
      headerName: 'Canal', 
      width: 150,
      valueFormatter: (value: string) => value || 'No especificado',
    },
    { 
      field: 'gec', 
      headerName: 'Gec', 
      width: 150,
      valueFormatter: (value: string) => value || 'No especificado',
    },
    { 
      field: 'start_date', 
      headerName: 'Fecha de inicio', 
      width: 180,
      valueFormatter: (value: string) => formatDate(value)
    },
    { 
      field: 'end_date', 
      headerName: 'Fecha de fin', 
      width: 180,
      valueFormatter: (value: string) => formatDate(value)
    },
    { 
      field: 'status', 
      headerName: 'ESTADO', 
      width: 150,
      renderCell: (params) => {
        const isActive = params.value === 'activo';
        
        const handleStatusChange = async (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
          e.stopPropagation();
          const newStatus = checked ? 'activo' : 'inactivo';
          
          // Optimistic update
          setRows(prevRows => 
            prevRows.map(row => 
              row.id === params.row.id 
                ? { ...row, status: newStatus } 
                : row
            )
          );
          setFilteredRows(prevRows => 
            prevRows.map(row => 
              row.id === params.row.id 
                ? { ...row, status: newStatus } 
                : row
            )
          );
          
          try {
            const result = await updatePromotionStatus(params.row.id, newStatus);
            if (!result.success) {
              // Revert on error
              setRows(prevRows => 
                prevRows.map(row => 
                  row.id === params.row.id 
                    ? { ...row, status: params.value } 
                    : row
                )
              );
              setFilteredRows(prevRows => 
                prevRows.map(row => 
                  row.id === params.row.id 
                    ? { ...row, status: params.value } 
                    : row
                )
              );
              enqueueSnackbar(result.res || 'Error al actualizar el estado', { variant: 'error' });
            } else {
              enqueueSnackbar('Estado actualizado correctamente', { variant: 'success' });
            }
          } catch (err) {
            console.error('Error updating status:', err);
            // Revert on error
            setRows(prevRows => 
              prevRows.map(row => 
                row.id === params.row.id 
                  ? { ...row, status: params.value } 
                  : row
              )
            );
            setFilteredRows(prevRows => 
              prevRows.map(row => 
                row.id === params.row.id 
                  ? { ...row, status: params.value } 
                  : row
              )
            );
            enqueueSnackbar('Error al actualizar el estado', { variant: 'error' });
          }
        };
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={isActive}
                onChange={handleStatusChange}
                color={isActive ? 'success' : 'default'}
                size="small"
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isActive ? 'success.main' : 'text.secondary',
                  fontWeight: 500,
                  minWidth: 70,
                  textAlign: 'center'
                }}
              >
                {isActive ? 'Activo' : 'Inactivo'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEditPromotion(params.row as Promotion)}
            title="Editar"
            sx={{ color: '#424242' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row as Promotion)}
            title="Eliminar"
            sx={{ color: '#424242' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    if (model.items.length === 0 || !model.items[0]?.value) {
      setFilteredRows(rows);
      return;
    }

    const filtered = rows.filter(row => {
      const { field, value } = model.items[0];
      if (!value) return true; // Si no hay valor de filtro, incluir todas las filas
      
      const rowValue = row[field as keyof PromotionRow];
      if (rowValue === null || rowValue === undefined) return false;
      
      try {
        const rowString = String(rowValue).toLowerCase();
        return rowString.includes(String(value).toLowerCase());
      } catch (error) {
        console.error('Error al filtrar:', error);
        return true; // En caso de error, incluir la fila
      }
    });
    setFilteredRows(filtered);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const handleRowClick = (params: any) => {
    // Handle row click if needed
    console.log('Row clicked:', params.row);
  };

  return (
    <Container maxWidth="xl" sx={{ 
      py: 4, 
      height: 'calc(100vh - 100px)',
      backgroundColor: 'white',
      borderRadius: 2,
      boxShadow: 1,
      '&.MuiContainer-root': {
        backgroundColor: 'white',
      }
    }}>
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h4" component="h1">
          Promociones
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar promociones..."
            value={searchText}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }}
            sx={{ width: 300, flexGrow: 1, maxWidth: 500 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              color: '#C8102E',
              backgroundColor: 'white',
              borderColor: '#C8102E',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#C8102E',
                color: 'white',
                borderColor: '#C8102E',
              },
              textTransform: 'none',
              minWidth: '180px',
              height: '40px',
              borderRadius: '4px',
            }}
          >
            Nueva Promoción
          </Button>
        </Box>
      </Box>
    
    <>
      <AddPromotionDialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
      />
      
      {editDialogOpen && selectedPromotion && (
        <EditPromotionDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          promotion={selectedPromotion}
          onSave={handleSavePromotion}
        />
      )}
      
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Está seguro de eliminar esta promoción?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            La promoción "{promotionToDelete?.title}" será eliminada permanentemente. 
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
            variant="contained"
            autoFocus
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
    
    {filteredRows.length === 0 ? (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="300px"
        sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 4 }}
      >
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No se encontraron promociones
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {searchText ? 'Intenta con otros términos de búsqueda' : 'No hay promociones disponibles en este momento'}
        </Typography>
      </Box>
      ) : (
        <Box sx={{ 
          height: 'calc(100vh - 200px)', 
          width: '100%',
          '& .MuiDataGrid-root': {
            backgroundColor: 'white',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiDataGrid-filterForm': {
              p: 1,
            },
            '& .MuiDataGrid-filterFormValueInput': {
              width: '100%',
            },
            '& .MuiDataGrid-filterFormDeleteIcon': {
              marginRight: 0,
            },
            '& .MuiDataGrid-toolbarContainer': {
              p: 1,
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
          },
        }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            getRowId={(row) => row.id}
            filterMode="server"
            onFilterModelChange={handleFilterModelChange}
            filterModel={filterModel}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                borderRight: 'none',
                padding: '8px 16px',
              },
              '& .MuiDataGrid-columnHeader': {
                borderRight: 'none',
                padding: '8px 16px',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
            }}
            slots={{
              toolbar: () => (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" component="div">
                    Lista de Promociones
                  </Typography>
                </Box>
              ),
            }}
            disableColumnMenu={false}
            disableColumnFilter={false}
            disableColumnSelector={false}
            localeText={{
              // Filter panel text
              filterPanelAddFilter: 'Agregar filtro',
              filterPanelRemoveAll: 'Eliminar todos',
              filterPanelDeleteIconLabel: 'Eliminar',
              filterPanelLogicOperator: 'Operador lógico',
              filterPanelOperator: 'Operador',
              filterPanelOperatorAnd: 'Y',
              filterPanelOperatorOr: 'O',
              filterPanelColumns: 'Columnas',
              filterPanelInputLabel: 'Valor',
              filterPanelInputPlaceholder: 'Valor del filtro',
              columnMenuLabel: 'Menú',
              columnMenuShowColumns: 'Mostrar columnas',
              columnMenuFilter: 'Filtrar',
              columnMenuHideColumn: 'Ocultar',
              
              // Filter operators text
              filterOperatorContains: 'contiene',
              filterOperatorEquals: 'igual a',
              filterOperatorStartsWith: 'comienza con',
              filterOperatorEndsWith: 'termina con',
              
              // Column header
              columnHeaderSortIconLabel: 'Ordenar'
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default PromotionsPage;
